/**
 * Page builder — server functions.
 *
 * Public: read a published page/region by path, and exchange a short-lived
 * preview token (the storefront runs on a different host from the Admin, so it
 * cannot see the admin session).
 * Authorised: list, create, edit drafts, publish, restore.
 *
 * Every action is checked on the server. "Can edit" and "can publish" are two
 * different permissions — hiding a button is never the security boundary.
 */
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  normalizePath,
  sanitizeDocument,
  sanitizeSeo,
  type PageDocument,
  type PageSeo,
} from "@/lib/cms/blocks";

export type CmsPageRow = {
  id: string;
  path: string;
  title: string;
  status: string;
  draft: PageDocument;
  published: PageDocument | null;
  seo: PageSeo;
  published_at: string | null;
  updated_at: string | null;
  has_previous: boolean;
  previous_published_at: string | null;
  version: number;
};

export type CmsCapabilities = { canEdit: boolean; canPublish: boolean; isAdmin: boolean };

export type CmsRevisionRow = {
  id: string;
  path: string;
  action: string;
  version: number;
  actor_label: string;
  created_at: string;
};

/** Raised when someone else saved in the meantime. */
export const CONFLICT_PREFIX = "CONFLICT:";

export const getPublishedCmsPage = createServerFn({ method: "GET" })
  .inputValidator((data: { path: string }) => ({ path: String(data.path) }))
  .handler(async ({ data }) => {
    const { loadPublishedCmsPage } = await import("@/lib/cms.server");
    return loadPublishedCmsPage(data.path);
  });

/* ------------------------------------------------------------------ */
/* Permissions                                                         */
/* ------------------------------------------------------------------ */

type Ctx = {
  supabase: {
    rpc: (n: string, a: Record<string, unknown>) => PromiseLike<{ data: unknown }>;
    from: (t: string) => never;
  };
  userId: string;
  claims?: Record<string, unknown>;
};

async function capabilitiesOf(context: Ctx): Promise<CmsCapabilities> {
  const [admin, edit, publish] = await Promise.all([
    context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" }),
    context.supabase.rpc("cms_can_edit", { _user_id: context.userId }),
    context.supabase.rpc("cms_can_publish", { _user_id: context.userId }),
  ]);
  const isAdmin = admin.data === true;
  return {
    isAdmin,
    canEdit: isAdmin || edit.data === true,
    canPublish: isAdmin || publish.data === true,
  };
}

async function requireEdit(context: Ctx): Promise<CmsCapabilities> {
  const caps = await capabilitiesOf(context);
  if (!caps.canEdit) throw new Error("You don’t have permission to edit the website");
  return caps;
}

async function requirePublish(context: Ctx): Promise<CmsCapabilities> {
  const caps = await capabilitiesOf(context);
  if (!caps.canPublish) {
    throw new Error("Only a publisher can make changes live. Your draft has been saved.");
  }
  return caps;
}

function actorLabel(context: Ctx): string {
  const c = context.claims ?? {};
  return String(c["email"] ?? c["phone"] ?? context.userId).slice(0, 160);
}

async function recordRevision(
  context: Ctx,
  input: { path: string; action: string; version: number; doc: PageDocument },
) {
  const db = context.supabase as unknown as {
    from: (t: string) => { insert: (v: unknown) => PromiseLike<{ error: unknown }> };
  };
  try {
    await db.from("cms_page_revisions").insert({
      path: input.path,
      action: input.action,
      version: input.version,
      doc: input.doc,
      actor_id: context.userId,
      actor_label: actorLabel(context),
    });
    await db.from("audit_log").insert({
      actor_id: context.userId,
      entity_type: "cms_page",
      entity_id: input.path,
      action: input.action,
      diff: { version: input.version },
    });
  } catch {
    /* history must never block the edit itself */
  }
}

export const adminCmsCapabilities = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<CmsCapabilities> =>
    capabilitiesOf(context as unknown as Ctx),
  );

/* ------------------------------------------------------------------ */
/* Reading                                                             */
/* ------------------------------------------------------------------ */

function rowOut(r: Record<string, unknown>): CmsPageRow {
  return {
    id: String(r["id"]),
    path: String(r["path"]),
    title: String(r["title"] ?? ""),
    status: String(r["status"] ?? "draft"),
    draft: sanitizeDocument(r["draft"]),
    published: r["published"] ? sanitizeDocument(r["published"]) : null,
    seo: sanitizeSeo(r["seo"]),
    published_at: (r["published_at"] as string | null) ?? null,
    updated_at: (r["updated_at"] as string | null) ?? null,
    has_previous: Boolean(r["previous_published"]),
    previous_published_at: (r["previous_published_at"] as string | null) ?? null,
    version: Number(r["version"] ?? 1),
  };
}

const SELECT =
  "id, path, title, status, draft, published, seo, published_at, updated_at, previous_published, previous_published_at, version";

export const adminListCmsPages = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<CmsPageRow[]> => {
    await requireEdit(context as unknown as Ctx);
    const { data, error } = await context.supabase
      .from("cms_pages")
      .select(SELECT)
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []).map((r) => rowOut(r as Record<string, unknown>));
  });

export const adminGetCmsPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { path: string }) => ({ path: String(data.path) }))
  .handler(async ({ data, context }): Promise<CmsPageRow | null> => {
    await requireEdit(context as unknown as Ctx);
    const { data: row, error } = await context.supabase
      .from("cms_pages")
      .select(SELECT)
      .eq("path", data.path)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return row ? rowOut(row as Record<string, unknown>) : null;
  });

export const adminListCmsRevisions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { path: string }) => ({ path: String(data.path) }))
  .handler(async ({ data, context }): Promise<CmsRevisionRow[]> => {
    await requireEdit(context as unknown as Ctx);
    const { data: rows, error } = await context.supabase
      .from("cms_page_revisions")
      .select("id, path, action, version, actor_label, created_at")
      .eq("path", data.path)
      .order("created_at", { ascending: false })
      .limit(50);
    if (error) throw new Error(error.message);
    return (rows ?? []) as CmsRevisionRow[];
  });

export const adminCreateCmsPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { path: string; title?: string }) => data)
  .handler(async ({ data, context }): Promise<CmsPageRow> => {
    await requireEdit(context as unknown as Ctx);
    const path = normalizePath(data.path);
    if (!path) throw new Error("Enter a valid page address");
    const { data: row, error } = await context.supabase
      .from("cms_pages")
      .insert({
        path,
        title: String(data.title ?? "Untitled").slice(0, 160) || "Untitled",
        draft: { blocks: [] },
        created_by: context.userId,
      })
      .select(SELECT)
      .single();
    if (error) {
      if (error.code === "23505") throw new Error("A page with that address already exists");
      throw new Error(error.message);
    }
    return rowOut(row as Record<string, unknown>);
  });

/* ------------------------------------------------------------------ */
/* Writing                                                             */
/* ------------------------------------------------------------------ */

export const adminSaveCmsDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (data: {
      path: string;
      title?: string;
      draft: unknown;
      seo?: unknown;
      expectedVersion?: number;
    }) => data,
  )
  .handler(async ({ data, context }) => {
    await requireEdit(context as unknown as Ctx);
    const path = String(data.path);
    const draft = sanitizeDocument(data.draft);

    const { data: existing } = await context.supabase
      .from("cms_pages")
      .select("version")
      .eq("path", path)
      .maybeSingle();

    const current = existing ? Number((existing as { version?: number }).version ?? 1) : 0;
    if (
      existing &&
      typeof data.expectedVersion === "number" &&
      data.expectedVersion !== current
    ) {
      throw new Error(
        `${CONFLICT_PREFIX}Someone else changed this page while you were editing. Reload to see their version before saving.`,
      );
    }

    const nextVersion = current + 1;
    const patch: Record<string, unknown> = {
      draft,
      version: nextVersion,
      updated_at: new Date().toISOString(),
    };
    if (data.title !== undefined) patch["title"] = String(data.title).slice(0, 160);
    if (data.seo !== undefined) patch["seo"] = sanitizeSeo(data.seo);

    // Upsert so an editable area that has no row yet is created on first save.
    const { error } = await context.supabase
      .from("cms_pages")
      .upsert({ path, ...patch }, { onConflict: "path" });
    if (error) throw new Error(error.message);

    await recordRevision(context as unknown as Ctx, {
      path,
      action: "draft_saved",
      version: nextVersion,
      doc: draft,
    });
    return { ok: true as const, draft, version: nextVersion };
  });

export const adminPublishCmsPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { path: string; expectedVersion?: number }) => data)
  .handler(async ({ data, context }) => {
    await requirePublish(context as unknown as Ctx);
    const path = String(data.path);
    const { data: row, error: readErr } = await context.supabase
      .from("cms_pages")
      .select("draft, published, published_at, version")
      .eq("path", path)
      .maybeSingle();
    if (readErr) throw new Error(readErr.message);
    if (!row) throw new Error("Page not found");

    const current = Number((row as { version?: number }).version ?? 1);
    if (typeof data.expectedVersion === "number" && data.expectedVersion !== current) {
      throw new Error(
        `${CONFLICT_PREFIX}Someone else changed this page while you were editing. Reload before publishing.`,
      );
    }

    const prior = (row as { published?: unknown }).published ?? null;
    const draft = sanitizeDocument((row as { draft?: unknown }).draft);
    const nextVersion = current + 1;
    const { error } = await context.supabase
      .from("cms_pages")
      .update({
        previous_published: prior as never,
        previous_published_at: (row as { published_at?: string | null }).published_at ?? null,
        published: draft,
        status: "published",
        published_at: new Date().toISOString(),
        published_by: context.userId,
        version: nextVersion,
      })
      .eq("path", path);
    if (error) throw new Error(error.message);

    await recordRevision(context as unknown as Ctx, {
      path,
      action: "published",
      version: nextVersion,
      doc: draft,
    });
    const { invalidateCmsCache } = await import("@/lib/cms.server");
    invalidateCmsCache();
    return { ok: true as const, version: nextVersion };
  });

export const adminUnpublishCmsPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { path: string }) => data)
  .handler(async ({ data, context }) => {
    await requirePublish(context as unknown as Ctx);
    const { error } = await context.supabase
      .from("cms_pages")
      .update({ status: "draft", published: null, published_at: null })
      .eq("path", String(data.path));
    if (error) throw new Error(error.message);
    const { invalidateCmsCache } = await import("@/lib/cms.server");
    invalidateCmsCache();
    return { ok: true as const };
  });

export const adminDeleteCmsPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { path: string }) => data)
  .handler(async ({ data, context }) => {
    const caps = await capabilitiesOf(context as unknown as Ctx);
    if (!caps.isAdmin) throw new Error("Only an admin can delete a page");
    const { error } = await context.supabase
      .from("cms_pages")
      .delete()
      .eq("path", String(data.path));
    if (error) throw new Error(error.message);
    const { invalidateCmsCache } = await import("@/lib/cms.server");
    invalidateCmsCache();
    return { ok: true as const };
  });

/**
 * "Restore previous version" — swaps the currently live content back to the
 * version that was live before the last publish, and drops it into the draft
 * so the editor shows exactly what visitors see.
 */
export const adminRestorePreviousCmsPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { path: string }) => ({ path: String(data.path) }))
  .handler(async ({ data, context }) => {
    await requirePublish(context as unknown as Ctx);
    const { data: row, error: readErr } = await context.supabase
      .from("cms_pages")
      .select("published, previous_published, version")
      .eq("path", data.path)
      .maybeSingle();
    if (readErr) throw new Error(readErr.message);
    if (!row) throw new Error("Page not found");
    const prev = (row as { previous_published?: unknown }).previous_published;
    if (!prev) throw new Error("There is no earlier version to restore");
    const restored = sanitizeDocument(prev);
    const nextVersion = Number((row as { version?: number }).version ?? 1) + 1;
    const { error } = await context.supabase
      .from("cms_pages")
      .update({
        draft: restored,
        published: restored,
        status: "published",
        published_at: new Date().toISOString(),
        published_by: context.userId,
        previous_published: ((row as { published?: unknown }).published ?? null) as never,
        version: nextVersion,
      })
      .eq("path", data.path);
    if (error) throw new Error(error.message);
    await recordRevision(context as unknown as Ctx, {
      path: data.path,
      action: "restored",
      version: nextVersion,
      doc: restored,
    });
    const { invalidateCmsCache } = await import("@/lib/cms.server");
    invalidateCmsCache();
    return { ok: true as const, draft: restored, version: nextVersion };
  });

/** Bring back any earlier entry from the history list. */
export const adminRestoreCmsRevision = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string }) => ({ id: String(data.id) }))
  .handler(async ({ data, context }) => {
    await requirePublish(context as unknown as Ctx);
    const { data: rev, error } = await context.supabase
      .from("cms_page_revisions")
      .select("path, doc")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!rev) throw new Error("That version is no longer available");
    const path = String((rev as { path: string }).path);
    const doc = sanitizeDocument((rev as { doc: unknown }).doc);

    const { data: row } = await context.supabase
      .from("cms_pages")
      .select("published, published_at, version")
      .eq("path", path)
      .maybeSingle();
    const nextVersion = Number((row as { version?: number } | null)?.version ?? 1) + 1;

    const { error: upErr } = await context.supabase
      .from("cms_pages")
      .update({
        draft: doc,
        published: doc,
        status: "published",
        published_at: new Date().toISOString(),
        published_by: context.userId,
        previous_published: ((row as { published?: unknown } | null)?.published ?? null) as never,
        previous_published_at:
          (row as { published_at?: string | null } | null)?.published_at ?? null,
        version: nextVersion,
      })
      .eq("path", path);
    if (upErr) throw new Error(upErr.message);
    await recordRevision(context as unknown as Ctx, {
      path,
      action: "restored",
      version: nextVersion,
      doc,
    });
    const { invalidateCmsCache } = await import("@/lib/cms.server");
    invalidateCmsCache();
    return { ok: true as const, path, doc };
  });

/* ------------------------------------------------------------------ */
/* Preview sessions (Admin host → storefront host)                     */
/* ------------------------------------------------------------------ */

export type PreviewBootstrap = {
  ok: boolean;
  sid: string;
  canEdit: boolean;
  canPublish: boolean;
  drafts: Record<string, PageDocument>;
  error?: string;
};

/** The editor asks for one of these, then loads the preview with the token. */
export const adminStartPreviewSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const caps = await requireEdit(context as unknown as Ctx);
    const { createPreviewSession } = await import("@/lib/cms-preview.server");
    return createPreviewSession({
      userId: context.userId,
      canEdit: caps.canEdit,
      canPublish: caps.canPublish,
    });
  });

/**
 * Called from inside the preview iframe (a different host, no admin session).
 * The token is the only credential and it is short-lived, single-purpose and
 * read-only: it never authorises a save or a publish.
 */
export const exchangePreviewSession = createServerFn({ method: "POST" })
  .inputValidator((data: { token: string }) => ({ token: String(data.token ?? "").slice(0, 200) }))
  .handler(async ({ data }): Promise<PreviewBootstrap> => {
    const { resolvePreviewSession } = await import("@/lib/cms-preview.server");
    return resolvePreviewSession(data.token);
  });
